import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatPrice, AIRLINE_LOGOS } from "@/lib/api";
import { CheckCircle, Envelope, Armchair, Suitcase } from "@phosphor-icons/react";
import { useSEO } from "@/lib/seo";
import { Stepper } from "@/pages/BookingFlow";

export default function BookingSuccess() {
  const { reference } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(null);

  useSEO({ title: "Booking Confirmed | TheCityPair", noindex: true });

  useEffect(() => {
    api.get(`/bookings/${reference}`).then(r => setB(r.data)).catch(e => setError(e.message));
  }, [reference]);

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>

      {/* Stepper — keeps the customer oriented after the Stripe redirect.
          Step 4 (Confirmation) is highlighted. */}
      <div className="px-6 md:px-12 lg:px-24 pt-10">
        <Stepper step={4} />
      </div>

      <div className="px-6 md:px-12 lg:px-24 pb-20 max-w-4xl mx-auto" data-testid="booking-success">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle size={48} weight="fill" className="text-[#C29B62]" />
          <p className="tcp-overline text-[#C29B62]">Booking Confirmed</p>
        </div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter leading-none text-[#1C1C1C] mb-6">
          Your seats are <em className="italic">held.</em>
        </h1>
        <p className="text-[#666460] text-lg leading-relaxed max-w-xl mb-12">
          A confirmation has been sent to your contact email. Our concierge will reach out within 24 hours with your e-tickets and any onward documentation.
        </p>

        {error && <p className="text-red-600">Could not load booking. Reference: {reference}</p>}

        {b && (
          <>
            <div className="bg-white border border-[#E0DDD6] p-8 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="tcp-overline text-[#666460] mb-2">TheCityPair Reference</p>
                  <p className="font-serif text-4xl md:text-5xl tracking-tighter" style={{ color: "#C29B62" }} data-testid="booking-ref">{b.reference}</p>
                </div>
                {b.booking_reference_airline && (
                  <div className="md:border-l md:border-[#E0DDD6] md:pl-6">
                    <p className="tcp-overline text-[#666460] mb-2">Airline PNR</p>
                    <p className="font-serif text-4xl md:text-5xl tracking-tighter text-[#1C1C1C]" data-testid="booking-pnr">{b.booking_reference_airline}</p>
                    <p className="text-xs text-[#666460] mt-2">Use this with the airline to check in or manage the booking.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E0DDD6] p-8 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <img src={AIRLINE_LOGOS[b.airline_code || "AL"]} alt="" className="w-10 h-10 object-contain" />
                <div>
                  <p className="text-sm font-semibold text-[#1C1C1C]">{b.airline}</p>
                  <p className="text-xs text-[#666460]">{b.cabin}</p>
                </div>
              </div>
              <p className="font-serif text-3xl text-[#1C1C1C]">{b.origin} → {b.destination}</p>
              <p className="text-sm text-[#666460] mt-1">Depart {b.depart_date}{b.return_date ? ` · Return ${b.return_date}` : ""}</p>
            </div>

            <div className="bg-white border border-[#E0DDD6] p-8 mb-6">
              <p className="tcp-overline text-[#666460] mb-3">Passengers ({b.passengers?.length})</p>
              {b.passengers?.map((p, i) => <p key={i} className="text-sm">{i + 1}. {p.first_name} {p.last_name}</p>)}
            </div>

            {(b.selected_seats?.length > 0 || b.services_summary?.length > 0) && (
              <div className="bg-white border border-[#E0DDD6] p-8 mb-6" data-testid="success-extras">
                <p className="tcp-overline text-[#666460] mb-3">Seats &amp; Extras</p>
                {b.selected_seats?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#666460] mb-2">Reserved seats</p>
                    <ul className="space-y-1">
                      {b.selected_seats.map((s, i) => (
                        <li key={`${s.designator}-${s.passenger_label ?? i}-${s.segment_label ?? ""}`} className="text-sm flex items-center gap-2" data-testid={`success-seat-${i}`}>
                          <Armchair size={14} className="text-[#C29B62]" />
                          <span className="font-semibold">{s.designator}</span>
                          <span className="text-[#666460]">· {s.passenger_label}{s.segment_label ? ` · ${s.segment_label}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {b.services_summary?.filter(s => s.type === "baggage").length > 0 && (
                  <div>
                    <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#666460] mb-2">Add-ons</p>
                    <ul className="space-y-1">
                      {b.services_summary.filter(s => s.type === "baggage").map((s) => (
                        <li key={`baggage-${s.id ?? s.description}`} className="text-sm flex items-center gap-2">
                          <Suitcase size={14} className="text-[#C29B62]" />
                          <span>{s.description}</span>
                          {s.unit_amount > 0 && <span className="text-[#666460] ml-auto">{formatPrice(s.unit_amount * s.quantity, s.currency)}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white border border-[#E0DDD6] p-8 mb-6 flex items-center justify-between">
              <p className="tcp-overline text-[#666460]">Total Paid</p>
              <p className="font-serif text-3xl" style={{ color: "#C29B62" }}>{formatPrice(b.price_total, b.currency)}</p>
            </div>
          </>
        )}

        <div className="flex flex-wrap gap-4 mt-8">
          <Link to="/" className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="back-home">Back to Home</Link>
          <a href="mailto:concierge@thecitypair.com" className="btn-outline-light bg-white text-[#1C1C1C] border-[#E0DDD6] text-xs tracking-[0.16em] uppercase inline-flex items-center gap-2"><Envelope size={14} /> Contact Concierge</a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
